/**
 * A promoted row excerpt as a canvas fragment: the excerpt node plus the entities its rows
 * connect and the EXCERPT_OF edges between them — a lone node says nothing. Landing follows
 * the comms excerpt's rule (active investigation, else a draft exploration).
 */

import { landExcerptInObjectSet } from '@/features/comms/excerpt-object-set'
import type { RowExcerpt } from '@/services/api/structuredIngest'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { emptyViewState, type GraphView } from '@/types/graph-view'

export function rowExcerptGraphView(excerpt: RowExcerpt, caseId: string): GraphView {
  const nodes: GraphView['nodes'] = [
    {
      id: excerpt.id,
      labels: ['RowExcerpt'],
      typeLabel: 'RowExcerpt',
      name: excerpt.note || excerpt.name,
      properties: {
        excerpt_id: excerpt.excerptId,
        structured_table: excerpt.structuredTable,
        row_indices: excerpt.rowIndices,
        row_count: excerpt.rowCount,
        ts_from: excerpt.tsFrom,
        ts_to: excerpt.tsTo,
        note: excerpt.note,
        created_by: excerpt.createdBy,
      },
    },
  ]
  const edges: GraphView['edges'] = []
  for (const anchor of excerpt.anchors) {
    nodes.push({
      id: anchor.id,
      labels: anchor.labels,
      typeLabel: anchor.labels[0],
      name: anchor.name ?? undefined,
      properties: {},
    })
    edges.push({
      id: anchor.edgeId,
      type: 'EXCERPT_OF',
      source: excerpt.id,
      target: anchor.id,
      properties: {},
    })
  }
  return {
    meta: {
      source: 'assistant',
      createdAt: new Date().toISOString(),
      caseId,
      label: 'Promoted rows',
    },
    nodes,
    edges,
    view: emptyViewState(),
  }
}

/** Land the excerpt where the analyst is looking; returns where it landed for the toast. */
export function landRowExcerpt(excerpt: RowExcerpt, caseId: string) {
  return landExcerptInObjectSet(
    getOrCreateInvestigationsStore({ caseId }),
    rowExcerptGraphView(excerpt, caseId),
    excerpt.note || excerpt.name
  )
}
